import React, { Component } from "react";
import { withModulesManager, withHistory, formatMessage } from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { ArrowBack as BackIcon, Edit as EditIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchFamilyEnrollment,
  submitFamilyEnrollment,
  reviewFamilyEnrollment,
  approveFamilyEnrollment,
  rejectFamilyEnrollment,
  turnToDraftFamilyEnrollment,
  deleteFamilyEnrollment,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import {
  familyFieldRows,
  enrollmentMetaRows,
  openCreatedEnrollmentEntity,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
});

class FamilyEnrollmentDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.match?.params?.enrollmentId !==
      this.props.match?.params?.enrollmentId
    ) {
      this.load();
    }
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) this.props.fetchFamilyEnrollment(this.props.modulesManager, uuid);
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/family");
    else window.location.href = "/enrollment/family";
  };

  handleEdit = () => {
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    const { history } = this.props;
    if (history) history.push(`/enrollment/family/${uuid}/edit`);
    else window.location.href = `/enrollment/family/${uuid}/edit`;
  };

  handleOpenCreated = () => {
    const { modulesManager, history, enrollment } = this.props;
    openCreatedEnrollmentEntity(modulesManager, history, "family", enrollment);
  };

  run = (action, payload) =>
    Promise.resolve(action(this.props.modulesManager, payload)).then(() =>
      this.load()
    );

  render() {
    const { classes, intl, enrollment } = this.props;
    if (!enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    const familyRows = familyFieldRows(enrollment.data);
    const metaRows = enrollmentMetaRows(enrollment);

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "familyEnrollment.detail") ||
                "Family Enrollment"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
          {enrollment.status === "DRAFT" && (
            <IconButton color="primary" onClick={this.handleEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onEdit={this.handleEdit}
              onOpenCreated={
                enrollment.createdFamily?.uuid
                  ? this.handleOpenCreated
                  : undefined
              }
              openCreatedLabel="Open created family"
              onSubmit={(p) => this.run(this.props.submitFamilyEnrollment, p)}
              onReview={(p) => this.run(this.props.reviewFamilyEnrollment, p)}
              onApprove={(p) => this.run(this.props.approveFamilyEnrollment, p)}
              onReject={(p) => this.run(this.props.rejectFamilyEnrollment, p)}
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftFamilyEnrollment, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deleteFamilyEnrollment, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Family details" rows={familyRows} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.familyEnrollmentDetail,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchFamilyEnrollment,
      submitFamilyEnrollment,
      reviewFamilyEnrollment,
      approveFamilyEnrollment,
      rejectFamilyEnrollment,
      turnToDraftFamilyEnrollment,
      deleteFamilyEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(mapStateToProps, mapDispatchToProps)(FamilyEnrollmentDetail)
      )
    )
  )
);
